// 메신저 이미지 미리보기 — 확대·축소 막대에 «현재 배율(%)» 한 칸을 덧붙인다.
// 설계: `patch_mm_image_zoom_percent.md`(마스터 2026-10-01 «현재 배율이 몇인지 안보인다»).
//
// 🔴 MM DOM 은 MM 소유다 — 셀렉터가 어긋나거나 오류가 나면 조용히 무동작(숫자만 안 보이고
//    MM 확대·축소는 그대로 쓴다). 버튼·휠·키보드 어느 길로 바꾸든 결과는 img 하나에 모인다.
// 🔴 배율은 «인라인 `img.style.transform`» 에서만 읽는다 — getComputedStyle 은 쓰지 않는다
//    (MM 의 등장 애니메이션 `image_preview-zoom-in` 이 250ms 동안 scale(.8) 을 깔아 둔다).
// 🔴 배율 1 이고 이동이 없으면 MM 이 transform 을 아예 안 쓴다 = 1(100%) 로 읽는다.
// 🔴 PDF 미리보기도 같은 막대를 쓰지만 캔버스라 `img.image_preview__image` 가 없다 → 숫자 없음.
export const LABEL_CLASS = 'gw-zoom-pct';
export const MODAL_SEL = '.file-preview-modal';
export const IMG_SEL = '.file-preview-modal img.image_preview__image';
export const BAR_SEL = '.file-preview-modal__zoom-bar';
export const COLUMN_SEL = '.modal-column';   // showZoomControls 일 때만 있는 버튼 줄
export const SYNC_THROTTLE_MS = 50;
// 🔴 pointer-events:none — 숫자가 −·돋보기·+ 클릭을 가로채지 않는다.
export const LABEL_STYLE = 'display:inline-flex;align-items:center;min-width:48px;margin-left:12px;'
  + 'font-size:13px;font-weight:600;color:rgba(255,255,255,.88);font-variant-numeric:tabular-nums;'
  + 'user-select:none;pointer-events:none';

// 'translate(-10px, 5px) scale(0.75)' → 0.75 · '' · 'translate(3px, 4px)' → 1.
// 🔴 scale 이 없거나 «유한한 양수»가 아니면(NaN·Infinity·0·음수) 1 — 숫자가 깨져 보이지 않게.
export function parseScale(transform) {
  let raw = '';
  try { raw = String(transform === undefined || transform === null ? '' : transform); } catch (e) { return 1; }
  const m = /scale\(\s*([^(),]+?)\s*\)/.exec(raw);
  if (!m) return 1;
  const v = Number(m[1]);
  return (Number.isFinite(v) && v > 0) ? v : 1;
}

export function formatPct(scale) {
  const v = (Number.isFinite(scale) && scale > 0) ? scale : 1;   // 값은 parseScale 이 이미 추렸다(안전망)
  return Math.round(v * 100) + '%';
}

// 지금 열려 있는 미리보기 한 벌을 보고 라벨을 맞춘다. 반환 = 쓴 텍스트(없으면 null).
// 🔴 img 와 막대를 «같은 모달 안»에서 찾는다 — 닫히는 모달이 잠깐 겹칠 때 섞이지 않게.
// 🔴 멱등 — 텍스트가 같으면 아무것도 쓰지 않는다(우리 쓰기가 옵저버를 다시 깨우는 되먹임 차단).
export function syncZoomLabel(doc) {
  if (!doc || typeof doc.querySelector !== 'function') return null;
  let img = null;
  let col = null;
  try {
    img = doc.querySelector(IMG_SEL);
    const modal = (img && typeof img.closest === 'function') ? img.closest(MODAL_SEL) : null;
    const bar = (modal || doc).querySelector(BAR_SEL);
    col = bar ? bar.querySelector(COLUMN_SEL) : null;
  } catch (e) { img = null; col = null; }
  let label = null;
  try { label = (col && img) ? col.querySelector('.' + LABEL_CLASS) : null; } catch (e) { label = null; }
  // 지금 쓸 한 개만 남기고 다 지운다(모달이 닫혔다·PDF 로 바뀌었다·옛 모달이 겹쳐 있다).
  try {
    const stale = doc.querySelectorAll('.' + LABEL_CLASS);
    for (let i = 0; i < stale.length; i += 1) {
      const el = stale[i];
      if (el !== label && el.parentNode) el.parentNode.removeChild(el);
    }
  } catch (e) { /* */ }
  if (!col || !img) return null;
  if (!label) {
    try {
      label = doc.createElement('span');
      label.className = LABEL_CLASS;
      label.setAttribute('title', '현재 배율');
      // 🔴 aria-live 는 넣지 않는다 — 휠로 연속 확대하면 읽어 주는 소리가 끊이지 않는다.
      // 🔴 style 속성에 통째로 박는다 — MM 의 막대 CSS 가 덮지 못하고, 주입값이 그대로 남는다.
      label.setAttribute('style', LABEL_STYLE);
      col.appendChild(label);   // −·돋보기·+ 오른쪽(줄의 마지막 칸)
    } catch (e) { return null; }
  }
  const text = formatPct(parseScale(img.style && img.style.transform));
  try { if (label.textContent !== text) label.textContent = text; } catch (e) { return null; }
  return text;
}

// doc 당 1회 설치. 반환 = dispose(옵저버·타이머 해제 + 넣은 라벨 제거). 이미 설치돼 있으면 null.
// 🔴 `attributes` + attributeFilter ['style'] 가 핵심이다 — 배율은 img 의 style 속성만 바뀌고
//    자식 목록은 그대로다(childList 만 보면 +·− 를 눌러도 숫자가 안 변한다).
//    class 는 메신저 본문에서 가장 잦은 변이라 보지 않는다.
export function setupMmZoomLabel(doc, options) {
  const opts = options || {};
  if (!doc || !doc.body || doc.__gwMmZoomLabel) return null;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : () => true;
  doc.__gwMmZoomLabel = true;
  let disposed = false;
  let timer = null;

  const apply = () => {
    if (disposed || !isActive()) return;
    try { syncZoomLabel(doc); } catch (e) { /* MM DOM 변동 — 무동작 */ }
  };

  const kick = () => {
    if (disposed || timer) return;   // 쓰로틀 — 휠로 연속 확대해도 50ms 에 한 번만 본다
    timer = setTimeout(() => {
      timer = null;
      apply();
    }, SYNC_THROTTLE_MS);
  };

  apply();   // 설치 즉시 1회(옵저버를 기다리지 않는다 — 이미 열려 있는 미리보기도 잡는다)

  let obs = null;
  try {
    const MO = (doc.defaultView && doc.defaultView.MutationObserver)
      || (typeof window !== 'undefined' ? window.MutationObserver : null);
    if (MO) {
      // 🔴 모달이 아니라 doc.body 에 건다 — 미리보기 모달은 열 때마다 새로 만들어진다.
      obs = new MO(kick);
      obs.observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }
  } catch (e) { obs = null; }

  return function dispose() {
    if (disposed) return;
    disposed = true;                 // 남은 콜백·타이머는 여기서부터 무동작
    try { if (obs) obs.disconnect(); } catch (e) { /* */ }
    if (timer) { clearTimeout(timer); timer = null; }
    try {
      const list = doc.querySelectorAll('.' + LABEL_CLASS);
      for (let i = 0; i < list.length; i += 1) {
        const el = list[i];
        if (el.parentNode) el.parentNode.removeChild(el);
      }
    } catch (e) { /* */ }
    try { doc.__gwMmZoomLabel = false; } catch (e) { /* */ }
  };
}
